import { useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import clsx from "clsx";
import {
  ValidationResult,
  ValidationMessage,
} from "../../utils/configValidator";

interface ValidationPanelProps {
  result: ValidationResult;
  onNavigate?: (path: string) => void;
}

export function ValidationPanel({ result, onNavigate }: ValidationPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState<"all" | "errors" | "warnings">("all");

  const totalErrors = result.errors.length;
  const totalWarnings = result.warnings.length;
  const totalIssues = totalErrors + totalWarnings;

  if (totalIssues === 0) {
    return (
      <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
        <CheckCircle className="w-5 h-5" />
        <span className="text-sm font-medium">Configurazione valida</span>
      </div>
    );
  }

  const filteredMessages =
    filter === "all"
      ? [...result.errors, ...result.warnings]
      : filter === "errors"
        ? result.errors
        : result.warnings;

  // Raggruppa per categoria (primo segmento del path)
  const groupedMessages = filteredMessages.reduce(
    (acc, msg) => {
      const category = getCategoryFromPath(msg.path);
      if (!acc[category]) acc[category] = [];
      acc[category].push(msg);
      return acc;
    },
    {} as Record<string, ValidationMessage[]>,
  );

  return (
    <div
      className={clsx(
        "border rounded-lg overflow-hidden",
        totalErrors > 0 ? "border-red-200" : "border-amber-200",
      )}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={clsx(
          "w-full px-4 py-3 flex items-center justify-between",
          totalErrors > 0 ? "bg-red-50" : "bg-amber-50",
        )}
      >
        <div className="flex items-center space-x-3">
          {totalErrors > 0 ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          )}
          <span
            className={clsx(
              "font-medium",
              totalErrors > 0 ? "text-red-800" : "text-amber-800",
            )}
          >
            {totalErrors > 0
              ? `${totalErrors} errori${totalWarnings > 0 ? ` e ${totalWarnings} avvisi` : ""}`
              : `${totalWarnings} avvisi`}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="bg-white">
          {/* Filter tabs */}
          <div className="flex border-b border-gray-200 px-4">
            <button
              onClick={() => setFilter("all")}
              className={clsx(
                "px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                filter === "all"
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              )}
            >
              Tutti ({totalIssues})
            </button>
            {totalErrors > 0 && (
              <button
                onClick={() => setFilter("errors")}
                className={clsx(
                  "px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                  filter === "errors"
                    ? "border-red-600 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                )}
              >
                Errori ({totalErrors})
              </button>
            )}
            {totalWarnings > 0 && (
              <button
                onClick={() => setFilter("warnings")}
                className={clsx(
                  "px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                  filter === "warnings"
                    ? "border-amber-600 text-amber-600"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                )}
              >
                Avvisi ({totalWarnings})
              </button>
            )}
          </div>

          {/* Messages grouped by category */}
          <div className="max-h-64 overflow-auto">
            {Object.entries(groupedMessages).map(([category, messages]) => (
              <div
                key={category}
                className="border-b border-gray-100 last:border-0"
              >
                <div className="px-4 py-2 bg-gray-50">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {category}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {messages.map((msg, index) => (
                    <ValidationMessageItem
                      key={`${msg.path}-${index}`}
                      message={msg}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ValidationMessageItem({
  message,
  onNavigate,
}: {
  message: ValidationMessage;
  onNavigate?: (path: string) => void;
}) {
  const isError = message.type === "error";

  return (
    <li
      className={clsx(
        "px-4 py-2 flex items-start space-x-3 hover:bg-gray-50",
        onNavigate && "cursor-pointer",
      )}
      onClick={() => onNavigate?.(message.path)}
    >
      {isError ? (
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
      ) : (
        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p
          className={clsx(
            "text-sm",
            isError ? "text-red-700" : "text-amber-700",
          )}
        >
          {message.message}
        </p>
        <p className="text-xs text-gray-400 font-mono truncate mt-0.5">
          {message.path || "(root)"}
        </p>
      </div>
    </li>
  );
}

/**
 * Inline validation badge per tabs/sezioni
 */
export function ValidationBadge({
  errors,
  warnings,
}: {
  errors: number;
  warnings: number;
}) {
  if (errors === 0 && warnings === 0) return null;

  if (errors > 0) {
    return (
      <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-100 text-red-700 rounded-full">
        {errors}
      </span>
    );
  }

  return (
    <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-amber-100 text-amber-700 rounded-full">
      {warnings}
    </span>
  );
}

/**
 * Inline error message per singolo campo
 */
export function FieldError({ message }: { message?: string }) {
  if (!message) return null;

  return (
    <p className="mt-1 text-xs text-red-600 flex items-center">
      <AlertCircle className="w-3 h-3 mr-1" />
      {message}
    </p>
  );
}

// Utility: estrae categoria dal path
function getCategoryFromPath(path: string): string {
  if (!path) return "Generale";

  const firstPart = path.split(".")[0].split("[")[0];

  const categoryLabels: Record<string, string> = {
    site: "Shell",
    routing: "Routing",
    pages: "Pagine",
    dataSources: "Data Sources",
  };

  return categoryLabels[firstPart] || firstPart;
}
